import i18n from 'i18n'
// module imports
import {
  apiUAccount,
  apiUCNDInfo,
  apiUUpdateCND,
  apiUGetCND,
} from 'modules/Api'
import { createAPIEnvironment, createUAEnvironment } from 'modules/Environment'
import { getPermission } from 'modules/Permissions'
import { setSIPDebug } from 'modules/Sip'
import { setDialerIsOpen } from 'modules/LocalStorage'
// constant imports
import { DIALER_MODE } from 'constants/General'
import * as S from 'constants/LocalStorage'
import * as P from 'constants/Permissions'
import { SPOG, WEBAPP, RCON } from 'constants/Analytics'
import * as C from './constants'

export const dialerReady = () => ({ type: C.DIALER_READY })

export const hydrateAPIConfig =
  (environment, apiConfig, version) => (dispatch) => {
    dispatch({
      type: C.CONFIG_HYDRATE,
      payload: {
        ...createAPIEnvironment({ environment, apiConfig, version }),
        language: i18n.language,
      },
    })

    return Promise.resolve()
  }

export const hydrateUAConfig = (uaData) => (dispatch) => {
  dispatch({
    type: C.CONFIG_HYDRATE,
    payload: {
      ...createUAEnvironment(uaData),
    },
  })

  return Promise.resolve()
}

export const appendOptions = (options) => (dispatch) => {
  dispatch({
    type: C.CONFIG_HYDRATE,
    payload: options,
  })
}

export const fetchNotificationsPermission =
  () => async (dispatch, getState) => {
    await dispatch({
      type: C.NOTIFICATIONS_PERMISSION_FETCH,
      async payload() {
        return getPermission(P.NOTIFICATIONS)
      },
    })
    return getState()?.config?.permissions[P.NOTIFICATIONS]
  }

export const fetchMicrophonePermission = () => async (dispatch, getState) => {
  await dispatch({
    type: C.MICROPHONE_PERMISSION_FETCH,
    async payload() {
      return getPermission(P.MICROPHONE)
    },
  })
  return getState()?.config?.permissions[P.MICROPHONE]
}

export const updatePermissions = (permission) => ({
  type: C.PERMISSIONS_UPDATE,
  payload: permission,
})

export const fetchCallerIds = () => ({
  type: C.CALLER_ID_FETCH,
  async payload() {
    return apiUGetCND()
  },
})

export const updateCallerId = (option, lineData) => {
  const lineItem =
    option &&
    lineData.find((ld) => {
      return ld.lineId === option.value
    })
  let lineItemData

  if (!lineItem) {
    lineItemData = {
      id: '',
      type: '',
      lineId: '0',
    }
  } else {
    lineItemData = lineItem
  }

  const { id, type, lineId } = lineItemData

  return {
    type: C.CALLER_ID_UPDATE,
    async payload() {
      return apiUUpdateCND(!!option, id, type, lineId)
    },
  }
}

export const changeLanguage = (language) => ({
  type: C.LANGUAGE_UPDATE,
  async payload() {
    await i18n.changeLanguage(language)
    S.setStorage(S.UNITE_LANGUAGE_KEY, language)
    return language
  },
})

export const updateTokenType = (tokenType) => ({
  type: C.TOKEN_TYPE_UPDATE,
  payload: tokenType,
})

export const fetchAccount = () => ({
  type: C.ACCOUNT_FETCH,
  async payload() {
    return apiUAccount()
  },
})

export const fetchCNDInfo = () => ({
  type: C.CND_FETCH,
  async payload() {
    return apiUCNDInfo()
  },
})

export const updateDebug = (debug) => (dispatch) => {
  setSIPDebug(debug.SIPVerbose)

  dispatch({
    type: C.DEBUG_UPDATE,
    payload: S.addStorageJSON(S.UNITE_DEVELOPMENT, debug),
  })
}

export const toggleDebugEnabled = () => (dispatch, getState) => {
  // check for first load
  const debug = S.getStorageJSON(S.UNITE_DEVELOPMENT, false)

  if (!debug) {
    S.setStorageJSON(S.UNITE_DEVELOPMENT, { enable: true })
  } else {
    S.addStorageJSON(S.UNITE_DEVELOPMENT, {
      enable: !getState().config.debug?.enable,
    })
  }

  dispatch({
    type: C.DEBUG_TOGGLE,
  })
}

export const clearDebug = () => ({
  type: C.DEBUG_CLEAR,
  payload: S.removeFromStorage(S.UNITE_DEVELOPMENT),
})

export const fetchDebug = () => (dispatch) => {
  const payload = S.getStorageJSON(S.UNITE_DEVELOPMENT, false)

  dispatch({ type: C.DEBUG_FETCH, payload })
}

export const setIsShown = (isShown) => (dispatch, getState) => {
  // don't set shown dialer if webapp has active sessions
  if (
    isShown &&
    !getState().config.modeFloat &&
    getState().sessions.list.filter(Boolean).length
  ) {
    return
  }

  setDialerIsOpen(isShown)

  dispatch({
    type: C.SET_IS_SHOWN,
    payload: isShown,
  })
}

export const setSizeMode = (fullSize = false) => ({
  type: C.SIZE_MODE_SET,
  payload: fullSize,
})

export const setMode = (isFloat = false) => ({
  type: C.MODE_SET,
  payload: isFloat,
})

export const setPosition = (position = { top: 0, right: 0 }) => ({
  type: C.POSITION_SET,
  payload: position,
})

export const setSize = (size = { height: '0px', width: '0px' }) => ({
  type: C.SIZE_SET,
  payload: size,
})

export const setConsumer = (consumer) => ({
  type: C.CONSUMER_SET,
  payload: consumer,
})

const getConsumer = (mode) => {
  switch (mode) {
    case RCON:
      return RCON
    case SPOG:
      return SPOG
    default:
      return WEBAPP
  }
}

export const setModeSizeAndPosition = (mode, position, size) => (dispatch) => {
  const isFloat = mode && mode !== DIALER_MODE.STANDARD

  // TODO SPOG remove when dialer 2 version will be ready
  dispatch(setConsumer(getConsumer(mode)))

  dispatch(setMode(isFloat))
  if (mode === SPOG) {
    dispatch(setSizeMode(false))
    dispatch(setSize(size))
  } else {
    dispatch(setSizeMode(isFloat))
  }

  if (isFloat) {
    dispatch(setPosition(position))
  }
}

export const setScalable = (scalable = false) => ({
  type: C.SCALABLE_SET,
  payload: scalable,
})

export const addSipAction = (actionName) => ({
  type: C.ADD_SIP_ACTION,
  payload: actionName,
})

export const removeSipAction = (actionName) => ({
  type: C.REMOVE_SIP_ACTION,
  payload: actionName,
})

export const removeAllSipActions = () => ({
  type: C.REMOVE_ALL_SIP_ACTIONS,
  payload: undefined,
})
